import SubscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { subscriptionTabsSelector } from '@mm/state/selectors/subscriptionSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import { useDebouncedFetchSearchQuery } from '../../../../../common/hooks/use-debounced-fetch-search-query';
import { ActiveSubscriptionTab } from './ActiveSubscriptionTab/ActiveSubscriptionTab';
import './SubscriptionTabsArea.pcss';

type OuterProps = {
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
};

type InnerProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

type TabProps = {
  subscriptionTab: SubscriptionTabDefinition;
  activeTab: SubscriptionTabDefinition;
  setActiveTab: (activeTabId: string) => void;
  totalTabs: number;
};

const Tab = (props: TabProps) => {
  const { subscriptionTab, activeTab, setActiveTab, totalTabs } = props;
  const classList = classnames('subscription-tabs-area__tab', {
    'subscription-tabs-area--active-tab':
      subscriptionTab.planCode === activeTab.planCode,
  });

  const isFutureSubscription =
    subscriptionTab.pages[0]?.subscription?.state === 'future';

  const tabName =
    subscriptionTab.shortName + (isFutureSubscription ? ' (Future)' : '');

  return (
    <div
      className={classList}
      onClick={() => setActiveTab(subscriptionTab.planCode)}
    >
      {tabName}
    </div>
  );
};

const useSubscriptionTabs = (props: Props) => {
  const { subscriptionTabs } = props;
  const firstTab = subscriptionTabs[0]
    ? subscriptionTabs[0].planCode
    : undefined;
  const [activeTabId, setActiveTabId] = useState(firstTab);
  const activeTab = useMemo(
    () => subscriptionTabs.find(tab => tab.planCode === activeTabId),
    [subscriptionTabs, activeTabId],
  );

  useEffect(() => {
    if (!activeTab) {
      if (subscriptionTabs[0]) {
        setActiveTabId(subscriptionTabs[0].planCode);
      }
    }
  }, [subscriptionTabs, activeTab]);

  return { subscriptionTabs, activeTab, setActiveTabId };
};

const getNoTabsText = (searchInput: string) => {
  if (searchInput) {
    return `No pages for search input: ${searchInput}`;
  }

  return 'No pages or active plans';
};

const SubscriptionTabsArea = (props: Props) => {
  const { searchInput, setSearchInput } = props;
  const { subscriptionTabs, activeTab, setActiveTabId } =
    useSubscriptionTabs(props);
  const company = useSelector((state: StoreState) =>
    activeCompanySelector(state),
  );
  const debouncedFetchSearchQuery = useDebouncedFetchSearchQuery();

  const onSearchInputChange = (searchInput: string) => {
    setSearchInput(searchInput);

    if (company) {
      debouncedFetchSearchQuery(company.id, searchInput);
    }
  };

  return (
    <div className="subscription-tabs-area">
      <div className="subscription-tabs-area__tabs">
        <div className="subscription-tabs-area__tabs-wrapper">
          {activeTab &&
            subscriptionTabs.map(subscriptionTab => (
              <Tab
                key={subscriptionTab.planCode}
                activeTab={activeTab}
                subscriptionTab={subscriptionTab}
                setActiveTab={setActiveTabId}
                totalTabs={subscriptionTabs.length}
              />
            ))}
        </div>

        <SearchInput
          className="subscription-tabs-area__search"
          placeholder="Search pages"
          onChange={event => onSearchInputChange(event.target.value)}
          value={searchInput}
          useDarkStyle
        />
      </div>

      {activeTab && (
        <ActiveSubscriptionTab
          planTab={activeTab}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
        />
      )}

      {!activeTab && (
        <div className="subscription-tabs-area__no-pages-text">
          {getNoTabsText(searchInput)}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  subscriptionTabs: subscriptionTabsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  clearAccountData: () => dispatch(SubscriptionCRUDActions.clearAccountData()),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(SubscriptionTabsArea);
